import { WalletPayment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { Box, capitalize } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parse, format, isValid as isValidDate } from 'date-fns';
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { deleteFromWallet } from '../../api/wallet/deleteFromWallet';
import { useWalletQuery } from '../../api/wallet/getWallet';
import PaymentTypeButton from '../../components/PaymentTypeButton';
import Button from '../../components/styledComponents/Button';
import { PaymentType } from '../../constants/enums';
import useAnalytics from '../../hooks/useAnalytics';
import useGetCustKey from '../../hooks/useGetCustKey';
import { getWalletAccount, getWalletCard } from '../../utils/wallet';
import AccountPaymentTypeForm from '../Payment/AccountPaymentTypeForm';
import CardPaymentTypeForm, {
  SubmitProps,
} from '../Payment/CardPaymentTypeForm';
import PaymentPage from '../Payment/components/PaymentPage';
import RemoveFromWalletConfirmationDialog from '../Payment/dialogs/RemoveFromWalletConfirmationDialog';

const getPaymentType = (walletAccount: WalletPayment): string => {
  if (walletAccount) {
    if ('card' in walletAccount) return PaymentType.CREDIT;
    if (walletAccount?.account?.type === 'CHKG') return PaymentType.CHECKING;
    if (walletAccount?.account?.type === 'SVG') return PaymentType.SAVINGS;
  }

  return '';
};

const formatExpirationDate = (expirationDate?: string): string => {
  if (!expirationDate) return '';
  const parsed = parse(expirationDate, 'MM/dd/yyyy', new Date());
  return parsed && isValidDate(parsed) ? format(parsed, 'MM/yy') : '';
};

const PaymentMethodActions = () => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = location.pathname.includes('edit');
  const walletAccount: WalletPayment = location.state?.walletAccount;
  const queryClient = useQueryClient();
  const custKey = useGetCustKey();

  const { data: walletData } = useWalletQuery(custKey);
  const deletePaymentFromWalletMutation = useMutation({
    mutationFn: deleteFromWallet,
  });

  const [text, setText] = useState('');
  const [open, setOpen] = useState<boolean>(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(
    getPaymentType(walletAccount)
  );

  const getContent = () => {
    let extra = { formValues: {}, text: '' };
    switch (selectedPaymentType) {
      case PaymentType.CREDIT: {
        const card = getWalletCard(walletAccount);
        if (card) {
          extra = {
            formValues: {
              name: card.holderName,
              cardNumber: `${'*'.repeat(12)}${card.number}`,
              expirationDate: formatExpirationDate(card.expirationDate),
              zipCode: card.zipCode,
              nickname: walletAccount?.shortName ?? '',
            },
            text: `${card?.type} ****${card?.number}`,
          };
        }
        return { title: 'debit/credit card', extra };
      }
      case PaymentType.CHECKING: {
        const accountInfo = getWalletAccount(walletAccount);
        if (accountInfo) {
          extra = {
            formValues: {
              name: accountInfo?.holderName,
              routingNumber: accountInfo?.institution?.routingNumber,
              accountNumber: `*******${accountInfo?.number}`,
              nickname: walletAccount?.shortName ?? '',
            },
            text: `checking ****${accountInfo.number}`,
          };
        }
        return { title: 'checking account', extra };
      }
      case PaymentType.SAVINGS: {
        const accountInfo = getWalletAccount(walletAccount);
        if (accountInfo) {
          extra = {
            formValues: {
              name: accountInfo?.holderName,
              routingNumber: accountInfo?.institution?.routingNumber,
              accountNumber: `*******${accountInfo?.number}`,
              nickname: walletAccount?.shortName ?? '',
            },
            text: `savings ****${accountInfo.number}`,
          };
        }
        return { title: 'savings account', extra };
      }
      default:
        return { title: 'payment method', extra: { text: '' } };
    }
  };

  const getAdditionalFormProps = () => {
    if (walletAccount) {
      const content = getContent();
      return {
        ...content.extra,
        removeFromWallet: () => {
          setText(capitalize(content.extra?.text?.toLowerCase()));
          setOpen(true);
        },
      };
    }
    return {};
  };

  const handleRemoveFromWallet = async () => {
    const message = `You have successfully removed ${capitalize(
      getContent().extra?.text.toLowerCase()
    )}.`;

    try {
      await deletePaymentFromWalletMutation.mutateAsync({
        requestBody: {
          ownerID: custKey,
          walletID: walletData?.walletID ?? '',
          account: {
            paymentAccountToken: walletAccount?.paymentAccountToken,
          },
        },
      });
      await queryClient.removeQueries({
        queryKey: ['wallet'],
      });
      track('Payment Methods Modified', {
        location: 'Feature Card',
        event_type: 'Option Selected',
        link: 'Remove from wallet',
      });
      navigate('/wallet', {
        state: { message, alertOpen: true },
      });
    } catch (err) {
      // Error handled by mutation
    }
  };

  const handleFormSubmit = async (submitArgs?: SubmitProps) => {
    const paymentType = submitArgs?.paymentType;
    const lastFour = submitArgs?.lastFour;
    await queryClient.removeQueries({
      queryKey: ['wallet'],
    });
    const message = `You have successfully ${
      isEditing ? 'updated' : 'saved'
    }  ${paymentType} ****${lastFour}.`;

    navigate('/wallet', {
      state: { message, alertOpen: true },
    });
  };

  const renderContent = (): React.ReactElement<any> => {
    const buttonText = isEditing ? 'Save' : 'Done';
    switch (selectedPaymentType) {
      case PaymentType.CREDIT:
        return (
          <CardPaymentTypeForm
            onSubmit={handleFormSubmit}
            showNicknameField
            buttonText={buttonText}
            cancelAddPaymentMethod={() => {
              if (walletAccount) {
                navigate('/wallet');
              }
              return setSelectedPaymentType('');
            }}
            showTermsAndConditions
            savePaymentToWallet={!getWalletCard(walletAccount)} // If we are not editing we have to save the payment method to the wallet
            paymentAccountToken={walletAccount?.paymentAccountToken}
            {...getAdditionalFormProps()}
          />
        );
      case PaymentType.CHECKING:
      case PaymentType.SAVINGS:
        return (
          <AccountPaymentTypeForm
            accountType={selectedPaymentType}
            onSubmit={handleFormSubmit}
            showNicknameField
            buttonText={buttonText}
            cancelAddPaymentMethod={() => {
              if (walletAccount) {
                navigate('/wallet');
              }
              return setSelectedPaymentType('');
            }}
            showTermsAndConditions
            savePaymentToWallet={!getWalletCard(walletAccount)} // If we are not editing we have to save the payment method to the wallet
            paymentAccountToken={walletAccount?.paymentAccountToken}
            {...getAdditionalFormProps()}
          />
        );
      default:
        return (
          <>
            <PaymentTypeButton
              data-testid="creditTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.CREDIT)}
            >
              Debit/credit card
            </PaymentTypeButton>
            <PaymentTypeButton
              data-testid="checkingTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.CHECKING)}
            >
              Checking account
            </PaymentTypeButton>
            <PaymentTypeButton
              data-testid="savingsTypeButton"
              onClick={() => setSelectedPaymentType(PaymentType.SAVINGS)}
            >
              Savings account
            </PaymentTypeButton>
            <Box
              sx={{
                '&.MuiBox-root': {
                  mt: { mobile: 2, tablet: 3 },
                  display: 'flex',
                  flexDirection: { mobile: 'column', tablet: 'row' },
                  justifyContent: 'flex-end',
                },
              }}
            >
              <Button
                component={Link}
                relative="path"
                to=".."
                variant="outlined"
                color="inherit"
                data-testid="addPaymentMethodSelectCancel"
                sx={{
                  borderWidth: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 18,
                  color: 'text.secondary',
                  px: 4,
                  py: 0.5,
                }}
                disableRipple
              >
                Cancel
              </Button>
            </Box>
          </>
        );
    }
  };

  return (
    <>
      <PaymentPage
        pageTitle={
          walletAccount
            ? `Edit ${getContent().title}`
            : `Add ${getContent().title}`
        }
      >
        {renderContent()}
      </PaymentPage>
      <RemoveFromWalletConfirmationDialog
        open={open}
        closeDialog={() => setOpen(false)}
        text={text}
        removeFromWallet={handleRemoveFromWallet}
      />
    </>
  );
};

export default PaymentMethodActions;
